import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import Keyboard3D from '../components/Keyboard3D';
import Panel from '../components/Panel';
import Knob from '../components/Knob';
import FilterPanel from '../components/FilterPanel';
import ADSREnvelopePanel from '../components/ADSREnvelopePanel';
import OscillatorPanel from '../components/OscillatorPanel';
import ArpeggiatorPanel from '../components/ArpeggiatorPanel';
import EffectsPanel from '../components/EffectsPanel';
import { useSynthContext } from '../hooks/useSynth';
import { DEFAULT_MASTER_VOLUME } from '../constants/synth';
import { COMMON_SPACING } from '../constants/spacing';

const SceneManager = ({ activeNotes, onNoteOn, onNoteOff }) => {
  const { camera } = useThree();
  const groupRef = useRef();
  const { synthParams, setSynthParams, synth } = useSynthContext();
  const [filterEnabled, setFilterEnabled] = useState(true);
  const [filterType, setFilterType] = useState(synthParams?.filter?.type || 'lowpass');
  const [filterFreq, setFilterFreq] = useState(synthParams?.filter?.frequency || 2000);
  const [filterQ, setFilterQ] = useState(synthParams?.filter?.Q || 1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (groupRef.current) {
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
    }
  }, [camera]);

  const handleNoteOn = useCallback((note, velocity) => {
    if (onNoteOn) {
      onNoteOn(note, velocity);
    }
  }, [onNoteOn]);

  const handleNoteOff = useCallback((note) => {
    if (onNoteOff) {
      onNoteOff(note);
    }
  }, [onNoteOff]);  return (    <group ref={groupRef}>
      {/* Primary Panel - slightly set back */}      <group position={isMobile ? [0, -0.5, -18.0] : [0, 1, -18.0]}>
        <Panel
          width={28}
          height={4.5}
          depth={0.5}
          color="#1a1a1a"
          border={true}
          borderColor="#333333"
          title="Aggregatrex 6000"
        >
          <OscillatorPanel />
          <ADSREnvelopePanel />
          <FilterPanel
            filterEnabled={filterEnabled}
            filterType={filterType}
            filterFreq={filterFreq}
            filterQ={filterQ}
            depth={0.2}
            onFilterTypeChange={(newType, newEnabled) => {
              setFilterEnabled(newEnabled);
              setFilterType(newType);
              setSynthParams((prevParams) => ({
                ...prevParams,
                filter: {
                  ...prevParams.filter,
                  type: newType,
                  enabled: newEnabled
                }
              }));
              if (synth && synth.setFilter) {
                if (newEnabled) {
                  synth.setFilter(newType, filterFreq, filterQ);
                } else {
                  if (synth.bypassFilter) {
                    synth.bypassFilter();
                  } else {
                    const bypassFreq = newType === 'lowpass' ? 20000 : 20;
                    synth.setFilter(newType, bypassFreq, 0.1);
                  }
                }
              }
            }}
            onFilterFreqChange={(frequency) => {
              setFilterFreq(frequency);
              setSynthParams((prevParams) => ({
                ...prevParams,
                filter: { ...prevParams.filter, frequency }
              }));
              if (synth && synth.setFilter && filterEnabled) {
                synth.setFilter(filterType, frequency, filterQ);
              }
            }}            onFilterQChange={(newQ) => {
              setFilterQ(newQ);
              setSynthParams((prevParams) => ({
                ...prevParams,          filter: { ...prevParams.filter, Q: newQ }              }));              if (synth && synth.setFilter && filterEnabled) {                synth.setFilter(filterType, filterFreq, newQ);          }            }}          />
            {/* Arpeggiator Panel */}
          <ArpeggiatorPanel />
            <group position={[0, 0, 0.1]}>
            <Knob
              size={COMMON_SPACING.MASTER_KNOB_SIZE}
              value={synthParams?.master?.volume ?? DEFAULT_MASTER_VOLUME}
              min={0}
              max={1}
              onChange={(value) => {
                setSynthParams((prevParams) => ({
                  ...prevParams,
                  master: {
                    ...prevParams.master,
                    volume: value,
                    isMuted: value === 0
                  }
                }));
                if (synth && synth.masterGain) {
                  const scaledVolume = value * value;
                  synth.masterGain.gain.setValueAtTime(
                    scaledVolume,
                    synth.audioContext.currentTime
                  );
                }
              }}
              label="MASTER VOL"
              color={synthParams?.master?.volume === 0 ? "#ff0000" : "#e91e63"}
              valueFormatter={(val) => {
                if (val === 0) return "MUTE";
                return `${Math.round(val * 100)}%`;
              }}
            />
          </group></Panel>      </group>      {/* Effects Panel - uniform distance */}
      <group position={isMobile ? [0, -0.5, -17.5] : [12, 1, -17.5]}>
        <EffectsPanel />
      </group>
      
      {/* Keyboard - uniform distance */}
      <group position={isMobile ? [0, -2.5, -12.0] : [0, -2, -12.0]}>
        <Keyboard3D
          startNote={36}
          endNote={96}
          onNoteOn={onNoteOn}
          onNoteOff={onNoteOff}
          activeNotes={activeNotes || new Set()}
        />
      </group>
    </group>
  );
};

export default SceneManager;
